import type { FC } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import { Image, VStack } from '@chakra-ui/react';
import { Typography } from '@shinetools/sunshine-universal';

import flyingBills from 'assets/brand/flying-bills.png';
import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Card from 'components/_core/SunshineCard';
import { StepProps } from 'components/Stepper/types';
import * as Layout from 'layouts/StickyStepperLayout';

import { formatText } from '../../../../../../../../components/RichText/utils';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from '../../../../contexts';
import routes from '../../../../routes';
import locales from './locales';

const formatStrong = (text: string) =>
  formatText(text, {
    renderStrong: (props) => (
      <Typography.Text bold variant="primary" {...props} />
    ),
  });

const InternationalTransferSuccess: FC<
  StepProps<BankTransferCreationStep>
> = () => {
  const { bankTransfer } = useBankTransferContext(
    'internationalTransferSuccess',
  );
  const {
    quote: {
      formattedEstimatedDeliveryAt,
      target: { amountFloat, currency },
    },
    recipient,
  } = bankTransfer;

  const amount = formatAmount({
    currency,
    currencyDisplay: 'code',
    type: 'payout',
    value: amountFloat,
  });
  const recipientName = recipient.fullName;

  const transferDescription = formattedEstimatedDeliveryAt
    ? locales.formatString(locales.description, {
        amount,
        date: formattedEstimatedDeliveryAt,
        recipientName,
      })
    : locales.formatString(locales.descriptionWithoutDate, {
        amount,
        recipientName,
      });

  return (
    <Layout.Root>
      <Layout.Body>
        <VStack as={Card.Group} padding={0} textAlign="center">
          <Card.Content align="center" justify="center">
            <VStack paddingX="space-32" paddingY="space-40" spacing="space-24">
              <Image alt={locales.title} boxSize="180px" src={flyingBills} />
              <VStack>
                <Heading size="lg">{locales.title}</Heading>
                <Typography.Text variant="secondary">
                  {formatStrong(transferDescription)}
                </Typography.Text>
              </VStack>
            </VStack>
          </Card.Content>
          <Card.Content align="center" justify="center">
            <VStack margin="auto" padding="space-24">
              <Button as={Link} to={routes.root} width={327}>
                {locales.finish}
              </Button>
            </VStack>
          </Card.Content>
        </VStack>
      </Layout.Body>
    </Layout.Root>
  );
};

export default InternationalTransferSuccess;
